import {
  KebabHorizontalIcon,
  PencilIcon,
  PlusIcon,
  SyncIcon,
  TrashIcon,
} from "@primer/octicons-react";
import {
  ActionList,
  ActionMenu,
  Box,
  Checkbox,
  IconButton,
  useConfirm,
} from "@primer/react";
import { useEffect, useState } from "react";
import TextTranslated from "../../components/text";
import Button from "../../components/ui/button";
import DataTable from "../../components/ui/data-table";
import { useCommonContext } from "../../contexts/common";
import { NotifyHelper } from "../../helpers/toast";
import { IKhoi } from "../../models/response/khoi/IKhoi";
import { khoiApi } from "../../api/khoiApi";
import KhoiDetailModal from "./KhoiDetailModal";
import { ComboboxTruong } from "../../components/combobox-truong";

const Khoi = () => {
  const [isShowModal, setIsShowModal] = useState(false);
  const [selectedId, setSelectedId] = useState(0);
  const [Khois, setKhois] = useState<IKhoi[]>([]);
  const [filteredKhois, setFilteredKhois] = useState<IKhoi[]>([]);
  const { translate } = useCommonContext();
  const [dm_truong_selected_id, setDmTruongSelectedId] = useState<number>(0);
  const confirm = useConfirm();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    handleReloadAsync();
  }, []);

  useEffect(() => {
    if (dm_truong_selected_id) {
      const filtered = Khois.filter(khoi => khoi.dm_truong_id === dm_truong_selected_id);
      setFilteredKhois(filtered);
    } else {
      setFilteredKhois(Khois);
    }
  }, [dm_truong_selected_id, Khois]);

  const handleReloadAsync = async () => {
    try {
      setIsLoading(true);
      const res = await khoiApi.selectAll();
      if (res.is_success) {
        setKhois(res.data);
        setFilteredKhois(res.data);
      } else {
        NotifyHelper.Error(res.message ?? "");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi xảy ra khi tải dữ liệu");
    } finally {
      setIsLoading(false);
    }
  };

  const handeDelete = async (id: number) => {
    if (
      await confirm({
        content: "Bạn có chắc chắn muốn xóa khối này?",
        title: "Lưu ý",
        cancelButtonContent: "Không xóa",
        confirmButtonContent: "Xóa khối",
        confirmButtonType: "danger",
      })
    ) {
      try {
        const res = await khoiApi.delete(id);
        if (res.is_success) {
          NotifyHelper.Success("Success");
          handleReloadAsync();
        } else {
          NotifyHelper.Error(res.message ?? "");
        }
      } catch (error) {
        NotifyHelper.Error("Có lỗi xảy ra khi xóa");
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <DataTable
        height={`${window.innerHeight - 250}px`}
        actionComponent={
          <Box
            sx={{
              display: "flex",
              gap: 2
            }}
          >
            <ComboboxTruong
              value={dm_truong_selected_id}
              onValueChanged={(value) => {
                setDmTruongSelectedId(value);
              }}
              isShowClearButton={true}
            />
            <Button
              text="Base.Label.Refresh"
              leadingVisual={SyncIcon}
              size="medium"
              onClick={handleReloadAsync}
              disabled={isLoading}
            />
            <Button
              text={translate("Base.Label.Create")}
              leadingVisual={PlusIcon}
              variant="primary" 
              size="medium"
              onClick={() => {
                setIsShowModal(true);
                setSelectedId(0);
              }}
              disabled={isLoading}
            />
          </Box>
        }
        columns={[
          {
            id: "cmd",
            width: "50px",
            caption: "#",
            align: "center",
            cellRender: (data: IKhoi) => {
              return (
                <ActionMenu>
                  <ActionMenu.Anchor>
                    <IconButton
                      icon={KebabHorizontalIcon}
                      variant="invisible"
                      aria-label="Open column options"
                    />
                  </ActionMenu.Anchor>

                  <ActionMenu.Overlay>
                    <ActionList>
                      <ActionList.Item
                        onSelect={() => {
                          setIsShowModal(true);
                          if (data.id !== undefined) {
                            setSelectedId(data.id);
                          }
                        }}
                      >
                        <ActionList.LeadingVisual>
                          <PencilIcon />
                        </ActionList.LeadingVisual>
                        <TextTranslated value="Base.Label.Edit" />
                      </ActionList.Item>
                      <ActionList.Divider />
                      <ActionList.Item
                        variant="danger"
                        onSelect={() => {
                          if (data.id !== undefined) {
                            handeDelete(data.id);
                          }
                        }}
                      >
                        <ActionList.LeadingVisual>
                          <TrashIcon />
                        </ActionList.LeadingVisual>
                        <TextTranslated value="Base.Label.Delete" />
                      </ActionList.Item>
                    </ActionList>
                  </ActionMenu.Overlay>
                </ActionMenu>
              );
            },
          },
          {
            dataField: "ma_khoi",
            width: "auto",
            caption: `Mã khối`,
            cellRender: (data: IKhoi) => {
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "baseline",
                    whiteSpace: "nowrap",
                  }}
                >
                  {data.ma_khoi}
                </Box>
              );
            },
          },
          {
            dataField: "ten_khoi",
            width: "auto",
            isMainColumn: true,
            caption: `Tên khối (Vi)`,
            cellRender: (data: IKhoi) => {
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "baseline",
                    whiteSpace: "nowrap",
                  }}
                >
                  {data.ten_khoi || "..."}
                </Box>
              );
            },
          },
          {
            dataField: "ten_khoi_en",
            width: "auto",
            caption: `Tên khối (En) `,
            cellRender: (data: IKhoi) => {
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "baseline",
                    whiteSpace: "nowrap",
                  }}
                >
                  {data.ten_khoi_en || "..."}
                </Box>
              );
            },
          },
          {
            dataField: "is_show_campaign",
            width: "120px",
            caption: "Show Campaign",
            align: "center",
            cellRender: (data: IKhoi) => {
              return (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                  }}
                >
                  <Checkbox 
          checked={data.is_show_campaign === 1 || data.is_show_campaign === true}
          disabled
                  />
                </Box>
              );
            },
          }
        ]}
        data={filteredKhois}
        title="Khối"
        subTitle={`${translate("Base.Label.TotalCount")}: ${filteredKhois.length}`}
        searchEnable
      />
      {isShowModal && (
        <KhoiDetailModal
        dm_truong_id={dm_truong_selected_id} 
          id={selectedId}
          onClose={() => {
            setIsShowModal(false);
          }}
          onSuccess={() => {
            handleReloadAsync();
            setIsShowModal(false);
          }}
        />
      )}
    </Box>
  );
};

export default Khoi;

